// src/store/userStore.js
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { auth, signInWithPopup, provider, signOut } from '../config/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import authService from '@/services/authService';
import useCartStore from './cartStore';

const MIN_PROFILE_FETCH_INTERVAL = 60 * 1000;
let lastProfileFetchTime = 0;

const useUserStore = create(
  persist(
    (set, get) => ({
      user: null,
      isLoggedIn: false,
      loading: true,
      termsAccepted: false,
      error: null,
      accessToken: null,  // JWT access token
      profileData: null,
      profileFetched: false,
      is_admin: false,

      isSubscriptionActive: false,
      subscription: '',
      subscriptionEnergy: 0,
      permanentEnergy: 0,
      purchases: [],
      stripeCustomerId: '',

      refreshTokenIfNeeded: async () => {
        const token = await authService.getValidToken();
        if (token) {
          set({ accessToken: token });
        }
        return token;
      },

      fetchWithTokenRefresh: async (url, options = {}) => {
        return authService.fetchWithAuth(url, options);
      },

      getFirebaseToken: async (forceRefresh = false) => {
        // Используется ТОЛЬКО для обмена на JWT
        return authService.getFirebaseToken(forceRefresh);
      },

      fetchUserProfile: async (forceRefresh = false) => {
        if (!get().isLoggedIn) {
          return null;
        }

        const now = Date.now();
        if (!forceRefresh && now - lastProfileFetchTime < MIN_PROFILE_FETCH_INTERVAL) {
          return get().profileData;
        }

        try {
          lastProfileFetchTime = now;
          const profileUrl = `${process.env.NEXT_PUBLIC_BACKEND_URL}/user_profile`;
          
          // Используем JWT для доступа к профилю
          const response = await get().fetchWithTokenRefresh(profileUrl);

          if (!response.ok) {
            const errorText = await response.text();
            console.error('Failed to fetch user profile:', errorText);
            return null;
          }

          const profileData = await response.json();

          set({
            profileData,
            profileFetched: true,
            user: {
              ...get().user,
              displayName: profileData.name || get().user?.displayName || profileData.email,
              photoURL: profileData.photo_url || profileData.avatar_url || get().user?.photoURL,
            },
            termsAccepted: profileData.is_terms_accepted,
            isLoggedIn: true,
            isSubscriptionActive: profileData.is_subscription_active || false,
            subscription: profileData.subscription || '',
            subscriptionEnergy: profileData.subscription_energy || 0,
            permanentEnergy: profileData.permanent_energy || 0,
            purchases: profileData.purchases || [],
            stripeCustomerId: profileData.stripe_customer_id || '',
            is_admin: profileData.is_admin || false,
          });

          return profileData;
        } catch (error) {
          console.error('Error fetching user profile:', error);
          return null;
        }
      },

      subscribeAuthChanges: () => {
        return onAuthStateChanged(auth, async (firebaseUser) => {
          // Проверяем флаг ручного выхода из системы
          const wasManualLogout = localStorage.getItem('manual_logout') === 'true';
          
          if (firebaseUser) {
            try {
              // Если был выполнен ручной выход, но Firebase все еще авторизован,
              // мы должны выйти из Firebase тоже
              if (wasManualLogout) {
                console.log('Обнаружен ручной выход из системы, выходим из Firebase...');
                localStorage.removeItem('manual_logout');
                await signOut(auth);
                return;
              }
              
              // Устанавливаем базовые данные пользователя из Firebase
              set({
                isLoggedIn: true,
                user: {
                  uid: firebaseUser.uid,
                  email: firebaseUser.email,
                  displayName: firebaseUser.displayName || firebaseUser.email,
                  photoURL: firebaseUser.photoURL,
                },
                loading: false,
              });

              try {
                // Обмениваем Firebase токен на JWT и получаем данные пользователя
                const authResult = await authService.exchangeFirebaseToken();
                
                if (authResult) {
                  set({
                    accessToken: authResult.accessToken, // Сохраняем JWT токен
                    profileData: authResult.user,
                    profileFetched: true,
                    isSubscriptionActive: authResult.user.is_subscription_active || false,
                    subscription: authResult.user.subscription || '',
                    subscriptionEnergy: authResult.user.subscription_energy || 0,
                    permanentEnergy: authResult.user.permanent_energy || 0,
                    purchases: authResult.user.purchases || [],
                    stripeCustomerId: authResult.user.stripe_customer_id || '',
                    is_admin: authResult.user.is_admin || false,
                    user: {
                      ...get().user,
                      displayName: authResult.user.name || get().user?.displayName || authResult.user.email,
                      photoURL: authResult.user.photo_url || authResult.user.avatar_url || get().user?.photoURL,
                    },
                    loading: false,
                    error: null,
                  });
                }
              } catch (error) {
                console.error('Error exchanging tokens:', error);
                set({
                  loading: false,
                  error: 'Failed to authenticate with server: ' + error.message,
                });
              }
            } catch (error) {
              set({
                loading: false,
                error: 'Failed to authenticate with server: ' + error.message,
              });
            }
          } else {
            // Очищаем данные при выходе
            const currentFunctions = preserveFunctions(get());
            
            set({
              user: null,
              isLoggedIn: false,
              loading: false,
              accessToken: null,
              profileData: null,
              profileFetched: false,
              isSubscriptionActive: false,
              subscription: '',
              subscriptionEnergy: 0,
              permanentEnergy: 0,
              purchases: [],
              stripeCustomerId: '',
              is_admin: false,
              error: null,
              ...currentFunctions,
            });
            
            // Убираем флаг ручного выхода, если пользователь уже вышел
            if (wasManualLogout) {
              localStorage.removeItem('manual_logout');
            }
          }
        });
      },

      loginWithGoogle: async () => {
        set({ loading: true, error: null });
        try {
          // Выполняем вход через Firebase
          await signInWithPopup(auth, provider);
          // После успешного входа в Firebase, exchangeFirebaseToken 
          // будет вызван в subscribeAuthChanges
        } catch (error) {
          set({
            loading: false,
            error: error.message,
          });
        }
      },

      logout: async () => {
        const currentFunctions = preserveFunctions(get());

        set({
          loading: true,
          error: null,
          ...currentFunctions,
        });

        try {
          console.log('🔄 Начинаем выход из системы в userStore');
          
          // Используем централизованную логику из authService
          const success = await authService.logout();
          
          // Очищаем состояние независимо от результата запроса к authService
          set({
            user: null,
            isLoggedIn: false,
            loading: false,
            accessToken: null,
            profileData: null,
            profileFetched: false,
            isSubscriptionActive: false,
            subscription: '',
            subscriptionEnergy: 0,
            permanentEnergy: 0,
            purchases: [],
            stripeCustomerId: '',
            is_admin: false,
            error: null,
            ...currentFunctions,
          });
          console.log('✅ Состояние пользователя сброшено');

          // Обрабатываем корзину после выхода
          if (typeof window !== 'undefined') {
            try {
              console.log('🔄 Сбрасываем состояние корзины');
              await useCartStore.getState().handleLogout();
              console.log('✅ Состояние корзины сброшено');
            } catch (error) {
              console.error('❌ Ошибка обработки корзины при выходе:', error);
            }
          }
          
          console.log('✅ Выход из системы успешно завершен');
          return success;
        } catch (error) {
          console.error('❌ Общая ошибка выхода из системы:', error);
          
          // Даже при ошибке очищаем все данные
          set({
            user: null,
            isLoggedIn: false,
            loading: false,
            accessToken: null,
            profileData: null,
            profileFetched: false,
            isSubscriptionActive: false,
            subscription: '',
            subscriptionEnergy: 0,
            permanentEnergy: 0,
            purchases: [],
            stripeCustomerId: '',
            is_admin: false,
            error: 'Logout error: ' + error.message,
            ...currentFunctions,
          });
          
          if (typeof window !== 'undefined') {
            try {
              await useCartStore.getState().handleLogout();
            } catch (cartError) {
              console.error('❌ Ошибка обработки корзины при восстановлении после ошибки:', cartError);
            }
          }
          
          return false;
        }
      },

      resetLoadingState: () => set({ loading: false }),

      resetStore: () => {
        const currentFunctions = preserveFunctions(get());

        try {
          signOut(auth).catch(() => null);
          authService.clearTokens();
          
          try {
            localStorage.clear();
          } catch (e) {
            // no-op
          }

          const initialState = {
            user: null,
            isLoggedIn: false,
            loading: false,
            termsAccepted: false,
            accessToken: null,
            profileData: null,
            profileFetched: false,
            isSubscriptionActive: false,
            subscription: '',
            subscriptionEnergy: 0,
            permanentEnergy: 0,
            purchases: [],
            stripeCustomerId: '',
            is_admin: false,
            error: null,
            ...currentFunctions,
          };

          set(initialState);
          
          if (typeof window !== 'undefined') {
            try {
              useCartStore.getState().handleLogout();
            } catch (error) {
              console.error('Error handling cart during resetStore:', error);
            }
          }

          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        } catch (e) {
          console.error("Severe error during reset:", e);
          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        }
      },

      acceptTerms: () => set({ termsAccepted: true }),

      checkCurrentAuth: () => {
        const currentUser = auth.currentUser;
        const storeState = get();
        return {
          firebaseUser: currentUser
            ? {
                uid: currentUser.uid,
                email: currentUser.email,
                displayName: currentUser.displayName,
              }
            : null,
          storeState,
        };
      },

      // Обновленная функция setUserManually для работы с серверной аутентификацией
      setUserManually: (userData) => {
        if (!userData) return;
        
        // Формируем объект пользователя из серверных данных
        const user = {
          uid: userData.id || userData.uid,
          id: userData.id,
          email: userData.email,
          displayName: userData.displayName || userData.name || userData.email,
          photoURL: userData.photo_url || userData.photoURL,
          // Добавляем остальные свойства
          ...userData
        };
        
        // Устанавливаем все необходимые данные в хранилище
        set({
          user,
          isLoggedIn: true,
          loading: false,
          error: null,
          termsAccepted: userData.is_terms_accepted || userData.termsAccepted || false,
          profileData: userData,
          profileFetched: true,
          isSubscriptionActive: userData.is_subscription_active || userData.isSubscriptionActive || false,
          subscription: userData.subscription || '',
          subscriptionEnergy: userData.subscription_energy || userData.subscriptionEnergy || 0,
          permanentEnergy: userData.permanent_energy || userData.permanentEnergy || 0,
          purchases: userData.purchases || [],
          stripeCustomerId: userData.stripe_customer_id || userData.stripeCustomerId || '',
          is_admin: userData.is_admin || userData.isAdmin || false
        });
      },

      // Хелперы для подписки
      hasActiveSubscription: () => {
        return get().isSubscriptionActive;
      },

      getTotalEnergy: () => {
        return get().subscriptionEnergy + get().permanentEnergy;
      },

      hasPurchased: (purchaseId) => {
        const purchases = get().purchases || [];
        return purchases.includes(purchaseId);
      },

      updateSubscriptionStatus: (isActive, subscriptionData = '', subscriptionEnergy = 0) => {
        set({
          isSubscriptionActive: isActive,
          subscription: subscriptionData,
          subscriptionEnergy,
        });

        const currentProfileData = get().profileData;
        if (currentProfileData) {
          set({
            profileData: {
              ...currentProfileData,
              is_subscription_active: isActive,
              subscription: subscriptionData,
              subscription_energy: subscriptionEnergy,
            },
          });
        }
      },

      addPurchase: (purchaseId) => {
        const currentPurchases = get().purchases || [];
        if (!currentPurchases.includes(purchaseId)) {
          const updatedPurchases = [...currentPurchases, purchaseId];
          set({ purchases: updatedPurchases });

          const currentProfileData = get().profileData;
          if (currentProfileData) {
            set({
              profileData: {
                ...currentProfileData,
                purchases: updatedPurchases,
              },
            });
          }
        }
      },

      isAdmin: () => {
        return get().is_admin;
      },

      checkAccessWithProfileRefresh: async (assetId, collection) => {
        const { isLoggedIn, purchases } = get();

        if (!isLoggedIn) {
          return false;
        }

        const profileData = await get().fetchUserProfile(true);

        if (!profileData) {
          return (
            checkPurchaseIncludes(purchases, assetId) ||
            checkPurchaseIncludes(purchases, collection, true) ||
            get().isSubscriptionActive
          );
        }

        const updatedPurchases = get().purchases || [];

        const hasAsset = checkPurchaseIncludes(updatedPurchases, assetId);
        const hasCollection = checkPurchaseIncludes(updatedPurchases, collection, true);

        return hasAsset || hasCollection || get().isSubscriptionActive;
      },
    }),
    {
      name: 'user-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        termsAccepted: state.termsAccepted,
      }),
    }
  )
);

// Вспомогательные функции
function preserveFunctions(state) {
  return {
    subscribeAuthChanges: state.subscribeAuthChanges,
    resetLoadingState: state.resetLoadingState,
    acceptTerms: state.acceptTerms,
    loginWithGoogle: state.loginWithGoogle,
    logout: state.logout,
    resetStore: state.resetStore,
    checkCurrentAuth: state.checkCurrentAuth,
    setUserManually: state.setUserManually,
    fetchUserProfile: state.fetchUserProfile,
    getFirebaseToken: state.getFirebaseToken,
    refreshTokenIfNeeded: state.refreshTokenIfNeeded,
    fetchWithTokenRefresh: state.fetchWithTokenRefresh,
    hasActiveSubscription: state.hasActiveSubscription,
    getTotalEnergy: state.getTotalEnergy,
    hasPurchased: state.hasPurchased,
    updateSubscriptionStatus: state.updateSubscriptionStatus,
    addPurchase: state.addPurchase,
    isAdmin: state.isAdmin,
    checkAccessWithProfileRefresh: state.checkAccessWithProfileRefresh,
  };
}

function checkPurchaseIncludes(purchases, itemId, isCollection = false) {
  return (purchases || []).some((p) => {
    if (typeof p === 'string') {
      if (isCollection) {
        return p === itemId || p === `collection:${itemId}`;
      }
      return p === itemId.toString();
    }
    if (typeof p === 'number') {
      return p.toString() === itemId.toString();
    }
    if (p && typeof p === 'object') {
      if (isCollection) {
        return (
          p.collection === itemId ||
          p.name === itemId ||
          (p.type === 'collection' && p.id === itemId)
        );
      }
      return p.id === itemId || p.assetId === itemId;
    }
    return false;
  });
}

export default useUserStore;